// Decides where the lobby "Jugar" cube sends the player:
// 1. the tournament they last joined, if it is still running;
// 2. the restarted copy of that tournament (cycle successor or same name/mode/size);
// 3. the first running tournament in their last chosen mode with the closest player count;
// otherwise the caller creates a Cienzar 1v1 match (10 min) that fills with a bot after 30 s.

const LAST_JOINED_TOURNAMENT_PREFIX = "xima_last_joined_tournament_v1_";
const QUICK_PLAY_PREFS_PREFIX = "xima_quick_play_v1_";
const ONLINE_TOURNAMENT_MODES = new Set(["free", "dice", "cienzar", "sciencevsazar"]);

export const LOBBY_QUICK_PLAY_DEFAULT_MATCH = {
  mode: "cienzar",
  memberSize: 2,
  havingTime: 10,
};

const MODE_ALIASES = {
  ciencia: "free",
  science: "free",
  free: "free",
  azar: "dice",
  luck: "dice",
  random: "dice",
  dice: "dice",
  cienzar: "cienzar",
  contrazar: "sciencevsazar",
  sciencevsazar: "sciencevsazar",
};

export const normalizeQuickPlayMode = (value) =>
  MODE_ALIASES[String(value || "").trim().toLowerCase()] || "";

const storageKeyForEmail = (prefix, email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return `${prefix}${normalizedEmail || "guest"}`;
};

const readJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return { tournamentId: raw };
    }
  } catch {
    return null;
  }
};

export const readLastJoinedTournament = (email) => {
  const stored = readJson(storageKeyForEmail(LAST_JOINED_TOURNAMENT_PREFIX, email));
  if (!stored || typeof stored !== "object") return null;
  const tournamentId = String(stored.tournamentId || stored.id || "").trim();
  if (!tournamentId) return null;
  return {
    tournamentId,
    tournamentName: String(stored.tournamentName || "").trim(),
    mode: normalizeQuickPlayMode(stored.mode),
    memberSize: Number(stored.memberSize) || 0,
  };
};

export const writeLastJoinedTournament = (email, tournament) => {
  const tournamentId = String(tournament?._id || tournament?.id || tournament?.tournamentId || "").trim();
  if (!tournamentId) return;
  try {
    window.localStorage.setItem(
      storageKeyForEmail(LAST_JOINED_TOURNAMENT_PREFIX, email),
      JSON.stringify({
        tournamentId,
        tournamentName: String(tournament?.tournamentName || "").trim(),
        mode: normalizeQuickPlayMode(tournament?.mode),
        memberSize: Number(tournament?.memberSize) || 0,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch {
    // Storage can be blocked; the cube then falls back to a new match.
  }
};

export const readQuickPlayPreferences = (email) => {
  const stored = readJson(storageKeyForEmail(QUICK_PLAY_PREFS_PREFIX, email));
  if (!stored || typeof stored !== "object") return null;
  return {
    mode: normalizeQuickPlayMode(stored.mode),
    memberSize: Number(stored.memberSize) || 0,
  };
};

const resolveEndMs = (record) => {
  const startMs = new Date(record?.startingTime || 0).valueOf();
  const quickExpiresAtMs = new Date(record?.quickMatchExpiresAt || 0).valueOf();
  const quickDurationMinutes = Math.max(0, Number(record?.quickMatchDurationMinutes) || 0);
  if (record?.isQuickMatch) {
    if (Number.isFinite(quickExpiresAtMs) && quickExpiresAtMs > 0) return quickExpiresAtMs;
    if (quickDurationMinutes > 0) return startMs + quickDurationMinutes * 60 * 1000;
  }
  return startMs + Number(record?.duration || 0) * 24 * 3600 * 1000;
};

const isRunningTournament = (record, nowMs) => {
  const startMs = new Date(record?.startingTime || 0).valueOf();
  if (!Number.isFinite(startMs) || startMs > nowMs) return false;
  return resolveEndMs(record) > nowMs;
};

// Only official online tournaments the player can enter without a password or a payment.
const isQuickPlayCandidate = (record, nowMs) =>
  record &&
  record.isSuperuserCreated === true &&
  !record.isIndependentMatch &&
  !record.isPuzzle &&
  !record.hasMatchPassword &&
  !(Number(record.tournamentRewardValue) > 0) &&
  ONLINE_TOURNAMENT_MODES.has(normalizeQuickPlayMode(record.mode)) &&
  isRunningTournament(record, nowMs);

const byNewest = (a, b) =>
  new Date(b?.createdAt || b?.startingTime || 0).valueOf() -
  new Date(a?.createdAt || a?.startingTime || 0).valueOf();

export const pickLobbyQuickPlayTournament = ({
  tournaments,
  lastJoined,
  preferences,
  nowMs = Date.now(),
}) => {
  const candidates = (Array.isArray(tournaments) ? tournaments : [])
    .filter((record) => isQuickPlayCandidate(record, nowMs))
    .sort(byNewest);
  const idOf = (record) => String(record?._id || record?.id || "").trim();

  if (lastJoined?.tournamentId) {
    const sameTournament = candidates.find((record) => idOf(record) === lastJoined.tournamentId);
    if (sameTournament) return { tournament: sameTournament, reason: "last" };

    const restarted =
      candidates.find(
        (record) => String(record?.cycleParentTournamentId || "").trim() === lastJoined.tournamentId
      ) ||
      (lastJoined.tournamentName
        ? candidates.find(
            (record) =>
              String(record?.tournamentName || "").trim() === lastJoined.tournamentName &&
              (!lastJoined.mode || normalizeQuickPlayMode(record.mode) === lastJoined.mode) &&
              (!lastJoined.memberSize || Number(record.memberSize) === lastJoined.memberSize)
          )
        : null);
    if (restarted) return { tournament: restarted, reason: "restarted" };
  }

  const hasHistory = Boolean(lastJoined?.tournamentId);
  const wantedMode = lastJoined?.mode || (hasHistory ? preferences?.mode : "");
  if (wantedMode) {
    const wantedSize = lastJoined?.memberSize || preferences?.memberSize || 2;
    const sameMode = candidates
      .filter((record) => normalizeQuickPlayMode(record.mode) === wantedMode)
      .sort(
        (a, b) =>
          Math.abs((Number(a.memberSize) || 2) - wantedSize) -
            Math.abs((Number(b.memberSize) || 2) - wantedSize) || byNewest(a, b)
      );
    if (sameMode[0]) return { tournament: sameMode[0], reason: "mode" };
  }

  return { tournament: null, reason: "create" };
};
